import React, {useState, useEffect, useRef} from "react";
import * as _ from "lodash";
import * as classes from "./WebcamVideo.module.css";

import PreprocessImg, {
  overlayOcrResults,
  overlayOcrResultsProjected,
} from "../../PreprocessImg";

import tesseractRecognize, {createTesseractScheduler} from "../../Tesseract";
import {loadModel, convertImgsToTensor, modelPredict} from "../../tf.js";
import solver from "../../sudoku-solver";

import {Canvas, CanvasCard} from "./Canvas";
import data from "./data";

// const factor = 0.5;
// const factor = 0.6;
// const factor = 0.75;
const factor = 1;

async function snapshot(
  src,
  vid,
  canvas,
  ctx,
  thresholdViewCanvas,
  contourViewCanvas,
  gridExtractViewCanvas,
  linesViewCanvas,
  adjGridExtractViewCanvas,
  extractCellViewCanvas
) {
  // if doesn't exists then break
  if (!canvas) throw new Error("no canvas found");
  canvas.width = vid.videoWidth * factor; // set its size to the one of the video
  canvas.height = vid.videoHeight * factor;
  ctx.drawImage(vid, 0, 0, canvas.width, canvas.height); // the video

  if (canvas.width > 0) {
    try {
      let imgData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      src.data.set(imgData);
      const output = PreprocessImg(
        src,
        //canvas,
        thresholdViewCanvas,
        contourViewCanvas,
        gridExtractViewCanvas,
        linesViewCanvas,
        adjGridExtractViewCanvas,
        extractCellViewCanvas,
        vid.videoWidth,
        vid.videoHeight,
        factor
      );
      if (output.cellValsImages.length > 0) {
        return output;
      } else {
        // throw new Error("no grid found");
      }
    } catch (err) {
      console.log(err);
      if (Number.isInteger(err)) {
        window.location.reload();
      }
      throw new Error("err from preprocess img");
    }
  } else {
    throw new Error("no output from preprocess img");
  }
}

export default function WebcamVideo() {
  const videoRef = useRef(null);
  const [videoFound, setVideoFound] = useState("Initializing...");

  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const thresholdViewCanvasRef = useRef(null);
  const contourViewCanvasRef = useRef(null);
  const gridExtractViewCanvasRef = useRef(null);
  const linesViewCanvasRef = useRef(null);
  const adjGridExtractViewCanvasRef = useRef(null);
  const extractCellViewCanvasRef = useRef(null);
  const overlayOcrResultsCanvasRef = useRef(null);

  const inversePerspectiveCanvasRef = useRef(null);
  // const overlayPerspectiveCanvasRef = useRef(null);

  const tfModel = useRef(null);
  const predictedCellsRef = useRef([]);
  const solutionRef = useRef(new Array(81).fill(0));

  const timeoutRef = useRef();
  const srcImgRef = useRef();

  useEffect(() => {
    async function loadTfModel() {
      tfModel.current = await loadModel();
    }
    loadTfModel();
  }, []);

  // initialize canvas
  useEffect(() => {
    // canvasRef.current = document.createElement("canvas"); // create a canvas
    ctxRef.current = canvasRef.current.getContext("2d"); // get its context

  }, []);


  const startVideo = () => {
    console.log("starting video");
    videoRef.current = document.querySelector("." + classes.Video);

    if (navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices
        .getUserMedia({video: true})
        .then(function (stream) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
          // srcImgRef.current = new window.cv.Mat(videoRef.current.videoHeight, videoRef.current.videoWidth, window.cv.CV_8UC4);
          console.log(videoRef.current.videoHeight);
          srcImgRef.current = new window.cv.Mat(480, 640, window.cv.CV_8UC4);
          // capRef.current = new window.cv.VideoCapture(videoRef.current);

          if (stream != null) {
            setVideoFound(null);
          } else {
            setVideoFound("No webcam found");
          }
        })
        .catch(function (error) {
          setVideoFound("No webcam found");
          console.log("Something went wrong with the webcam!");
        });
    }

    return function () {
      if (videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(function (track) {
          track.stop();
        });
      }
    };
  }

  // initialize video
  useEffect(startVideo, []);

  const processVideo = () => {


    const FPS = 15;
    let begin = Date.now();
    snapshot(
      srcImgRef.current,
      videoRef.current,
      canvasRef.current,
      ctxRef.current,
      thresholdViewCanvasRef.current,
      contourViewCanvasRef.current,
      gridExtractViewCanvasRef.current,
      linesViewCanvasRef.current,
      adjGridExtractViewCanvasRef.current,
      extractCellViewCanvasRef.current
    )
      .then(async ({cellValsImages, M}) => {
        var t0 = performance.now();
        let tensor2d = await convertImgsToTensor(
          cellValsImages,
          adjGridExtractViewCanvasRef.current
        );
        let results = await modelPredict(tfModel.current, tensor2d);
        tensor2d.dispose();
        // let results = [];
        const arr = new Array(81);
        for (let i = 0; i < results.length; i += 10) {
          let resultSubset = results.slice(i, i + 10);
          const maxIdx = resultSubset.indexOf(Math.max(...resultSubset));
          if (resultSubset[maxIdx] >= 0.4) {
            arr[i / 10] = maxIdx;
          } else {
            arr[i / 10] = 0;
          }
        }
        var t1 = performance.now();
        console.log("recognizing took " + (t1 - t0) + " ms");
        return {arr: arr, M: M};
      })
      .then(async ({arr, M}) => {
        // if predicted cells haven't changed, then no need to
        // re-solve puzzle
        // let solution = new Array(81).fill(0);
        let solution;
        let alreadySolved = solutionRef.current.reduce((a, b) => a + b, 0) !== 0;
        if (_.isEqual(predictedCellsRef.current, arr) & alreadySolved) {
          // no need to re-solve puzzle
          // console.log("predicted cells didnt change");
          solution = solutionRef.current;
        } else {
          // console.log("predicted cells changed");
          predictedCellsRef.current = arr;

          var t0 = performance.now();
          let gridVals = [];
          let row = [];
          // convert results into 9 x 9 matrix
          for (let i = 1; i <= arr.length; ++i) {
            row.push(arr[i - 1]);
            if (i % 9 === 0) {
              gridVals.push(row);
              row = [];
            }
          }
          // limit recursive calls to prevent unsolvable or very challenging slow cases
          window.numRecursiveCalls = 0;
          const maxRecursiveCalls = 10000;
          await (async () => {
            console.log("trying to solve puzzle...");
            solver(gridVals, {}, maxRecursiveCalls, false);
            solution = gridVals.flat();
          })();

          var t1 = performance.now();
          if (solution.includes(0)) {
            // console.log(solution);
            solution = new Array(81).fill(0);
            // console.log("unable to solve in " + (t1 - t0) + " ms");
          } else {
            console.log(
              "solved in " +
              (t1 - t0) +
              " milliseconds with " +
              window.numRecursiveCalls +
              " recursive calls."
            );
            solutionRef.current = solution;
          }
        }
        return {arr: arr, M: M, solution: solution};
      })
      .then(({arr, M, solution}) => {
        var t0 = performance.now();
        overlayOcrResults(
          extractCellViewCanvasRef.current,
          gridExtractViewCanvasRef.current,
          overlayOcrResultsCanvasRef.current,
          arr,
          solution
        );
        overlayOcrResultsProjected(
          canvasRef.current,
          canvasRef.current,
          inversePerspectiveCanvasRef.current,
          M,
          arr,
          solution
        );
        var t1 = performance.now();
        // console.log("overlayed in" + (t1 - t0) + " milliseconds");
      })
      .catch((err) => {
        console.log(err);

      });


    let delay = 1000 / FPS - (Date.now() - begin);
    timeoutRef.current = setTimeout(processVideo, delay);
  };

  useEffect(() => {
    setTimeout(() => {
      processVideo();
    }, 100);

    return function () {
      console.log("unmount and clear timeout");
      clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <>
      <h1>{videoFound}</h1>
      <div className={classes.Container}>
        <video className={classes.Video} ></video>
      </div>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justfiyContent: "center",
          alignItems: "center",
        }}
      >
        <div className={classes.MainCanvas}>
          <Canvas canvasRef={canvasRef}></Canvas>
        </div>
        <div className={classes.Info}>
          <h1>Algorithm Overview</h1>
          <p>
            Interactively view the steps that this app uses to process the image
            using OpenCV, classify the digits using Tensorflow, and then solve
            the puzzle using a recursive backtracking algorithm.
          </p>
        </div>
        <div style={{width: "100%"}}>
          <CanvasCard
            canvasRef={thresholdViewCanvasRef}
            data={data.threshold}
          />
          <CanvasCard canvasRef={contourViewCanvasRef} data={data.contour} />
          <CanvasCard
            canvasRef={extractCellViewCanvasRef}
            data={data.perspectiveTransform}
          />
          <CanvasCard
            canvasRef={gridExtractViewCanvasRef}
            data={data.prediction}
          />
          <CanvasCard
            canvasRef={overlayOcrResultsCanvasRef}
            data={data.solver}
          />
          <CanvasCard
            canvasRef={inversePerspectiveCanvasRef}
            data={data.perspectiveTransformInverse}
          />
        </div>
        {/* <canvas */}
        {/*   /\* style={{ width: 28, height: 28 }} *\/ */}
        {/*   ref={linesViewCanvasRef} */}
        {/* ></canvas> */}
        <div ref={linesViewCanvasRef}></div>
      </div>
    </>
  );
}
